import { useCallback, useEffect, useRef, useState } from "react";

const currentYm = () => new Intl.DateTimeFormat("en-CA", {
  timeZone:"Asia/Tokyo", year:"numeric", month:"2-digit",
}).format(new Date()).slice(0, 7);
const requestId = () => window.crypto?.randomUUID?.()
  || "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, character => {
    const value = Math.floor(Math.random() * 16);
    return (character === "x" ? value : (value & 3) | 8).toString(16);
  });
const STATUS = { unpaid:"未請求", billed:"請求済み", overdue:"未収", paid:"入金済み" };
const PAYMENT = { bank_transfer:"銀行振込", cash:"現金手渡し" };
const STORE = { kasama:"笠間", ami:"阿見", "re-five":"Re:ファイブ" };
const time = value => value ? String(value).replace(/^([0-9]{4})-([0-9]{2})-([0-9]{2})[ T]([0-9]{2}):([0-9]{2}).*/, "$1/$2/$3 $4:$5") : "—";
const amountInputStyle = {
  appearance:"textfield",
  boxSizing:"border-box",
  fontSize:18,
  fontWeight:800,
  lineHeight:1.25,
  minHeight:44,
  padding:"10px 12px",
  width:"min(100%, 18rem)",
};
const preventAmountStepKey = event => {
  if (["ArrowUp", "ArrowDown"].includes(event.key)) event.preventDefault();
};
const personStoreAmount = (person, storeId) => (person.store_amounts || [])
  .find(row=>row.store_id === storeId)?.amount ?? "";
const amountValue = value => value === null || value === undefined ? "" : String(value);
const confirmDiscardChanges = () => window.confirm(
  "未保存の入力があります。\n「OK」で変更を破棄して移動し、「キャンセル」で編集を続けます。",
);
const confirmResetApproval = () => window.confirm(
  "承認のみ取り消し、入力金額は残ります。\n承認を取り消しますか？",
);
const confirmNoCharge = storeName => window.confirm(
  `${storeName}を利用なし・請求なし（0円）として確定します。\n他施設の金額は変更しません。よろしいですか？`,
);

export default function BillingPersonMonthlySimple({ request, storeId, yearMonth, onYearMonthChange, onDirtyChange }) {
  const [localYearMonth, setLocalYearMonth] = useState(currentYm);
  const ym = yearMonth ?? localYearMonth;
  const [data, setData] = useState({ persons:[], invoice_month:null });
  const [amounts, setAmounts] = useState({});
  const [payments, setPayments] = useState({});
  const [expandedPersonId, setExpandedPersonId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const loadSequenceRef = useRef(0);

  useEffect(() => { setExpandedPersonId(null); }, [storeId, ym]);

  const load = useCallback(async () => {
    const sequence = ++loadSequenceRef.current;
    if (!storeId) { setData({ persons:[], invoice_month:null }); return; }
    setData({ persons:[], invoice_month:null });
    setAmounts({});
    setLoading(true); setError("");
    try {
      const next = await request(`/billing_person_months.php?ym=${encodeURIComponent(ym)}&store_id=${encodeURIComponent(storeId)}`);
      if (sequence !== loadSequenceRef.current) return;
      if (next.selected_store_id && next.selected_store_id !== storeId) {
        throw new Error("選択施設と取得データが一致しません。再読込してください。");
      }
      setData(next);
      setAmounts(Object.fromEntries((next.persons || []).map(person => {
        const storeAmount = (person.store_amounts || []).find(row=>row.store_id === storeId);
        return [person.id, storeAmount?.amount ?? ""];
      })));
    } catch (requestError) { if (sequence === loadSequenceRef.current) setError(requestError.message); }
    finally { if (sequence === loadSequenceRef.current) setLoading(false); }
  }, [request, storeId, ym]);
  useEffect(() => {
    load();
    return () => { loadSequenceRef.current += 1; };
  }, [load]);

  const dirtyPersonIds = (data.persons || [])
    .filter(person=>amountValue(amounts[person.id]) !== amountValue(personStoreAmount(person, storeId)))
    .map(person=>person.id);
  const hasUnsavedChanges = dirtyPersonIds.length > 0;
  const billingProhibited = Boolean(data.billing_policy?.billing_prohibited);
  const canFinalize = Boolean(data.capabilities?.can_finalize);
  useEffect(() => {
    onDirtyChange?.(hasUnsavedChanges);
    return () => onDirtyChange?.(false);
  }, [hasUnsavedChanges, onDirtyChange]);

  const discardPersonChanges = personId => {
    const person = (data.persons || []).find(row=>Number(row.id) === Number(personId));
    if (!person) return;
    setAmounts(current=>({ ...current, [person.id]:personStoreAmount(person, storeId) }));
  };
  const togglePerson = personId => {
    const closingCurrent = Number(expandedPersonId) === Number(personId);
    if (expandedPersonId !== null && dirtyPersonIds.some(id=>Number(id) === Number(expandedPersonId))) {
      if (!confirmDiscardChanges()) return;
      discardPersonChanges(expandedPersonId);
    }
    setExpandedPersonId(closingCurrent ? null : personId);
  };

  const mutate = async body => {
    setLoading(true); setError("");
    try { await request("/billing_person_months.php", "PUT", { ...body, request_id:requestId() }); await load(); }
    catch (requestError) { setError(requestError.message); setLoading(false); }
  };
  const sync = async () => {
    setLoading(true); setError("");
    try { await request("/billing_person_months.php", "POST", { action:"sync", ym, input_mode:"manual_store_total", request_id:requestId() }); await load(); }
    catch (requestError) { setError(requestError.message); setLoading(false); }
  };

  if (!storeId) return <section aria-label="人物月次簡易実費請求"><div>施設を選択してください。</div></section>;
  return <section aria-label="人物月次簡易実費請求">
    <style>{`.person-billing-amount-input::-webkit-inner-spin-button,.person-billing-amount-input::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}`}</style>
    <div style={{ display:"flex", gap:8, alignItems:"end", flexWrap:"wrap", marginBottom:12 }}>
      {yearMonth === undefined && <label>対象月<input aria-label="人物月次対象月" type="month" value={ym} onChange={event=>{
        const next = event.target.value;
        if (hasUnsavedChanges && !confirmDiscardChanges()) { event.target.value = ym; return; }
        setLocalYearMonth(next);
        onYearMonthChange?.(next);
      }} /></label>}
      {yearMonth === undefined && <strong>{STORE[storeId] || storeId}</strong>}
      <button type="button" onClick={sync} disabled={loading}>対象人物を準備</button>
      <button type="button" onClick={load} disabled={loading}>再読込</button>
    </div>
    {billingProhibited && <div role="status" style={{ marginBottom:10, padding:"10px 12px", border:"1px solid #f59e0b", borderRadius:8, background:"#fffbeb", color:"#92400e", fontWeight:800 }}>2026年9月は練習月・請求禁止です。金額入力と社長最終承認はできますが、請求・入金・月締め処理はできません。</div>}
    {error && <div role="alert" style={{ color:"#991b1b", marginBottom:10 }}>{error}</div>}
    {!data.invoice_month && !loading && <div>この月の人物月次データは未作成です。</div>}
    <div style={{ display:"grid", gap:10 }}>
      {(data.persons || []).map(person => {
        const terminalApproval = ["amount_confirmed","no_charge"].includes(person.status);
        const approved = terminalApproval && !person.stale;
        const locked = approved || person.billing_status !== "unpaid" || data.invoice_month?.status === "closed";
        const storeAmount = (person.store_amounts || []).find(row=>row.store_id === storeId);
        const legacyAmount = person.input_mode === "manual_person_total" && person.entered_amount !== null;
        const compatible = ["manual_store_total","manual_person_total"].includes(person.input_mode);
        const expanded = Number(expandedPersonId) === Number(person.id);
        const buttonId = `person-billing-toggle-${person.id}`;
        const detailId = `person-billing-detail-${person.id}`;
        return <article key={person.id} style={{ border:"1px solid #dbe3ea", borderRadius:10, overflow:"hidden", background:"#fff" }}>
          <button id={buttonId} type="button" aria-expanded={expanded} aria-controls={detailId} onClick={()=>togglePerson(person.id)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, width:"100%", minHeight:48, padding:"11px 12px", border:0, background:"#fff", color:"#0f172a", textAlign:"left", cursor:"pointer", font:"inherit" }}>
            <strong>{person.person_name_snapshot}</strong><span aria-hidden="true" style={{ flex:"0 0 auto", fontSize:18 }}>{expanded ? "▲" : "▼"}</span>
          </button>
          {expanded && <div id={detailId} role="region" aria-labelledby={buttonId} style={{ padding:"0 12px 12px", borderTop:"1px solid #eef2f7" }}>
          <div style={{ marginTop:10, fontSize:12, color:"#475569" }}>{STATUS[person.billing_status] || person.billing_status}</div>
          {!compatible ? <div role="alert">この行は詳細集計モードです。施設別簡易入力と併用できません。</div> : legacyAmount ? <div role="alert">既存の人物月額合計があるため施設別へ自動按分できません。</div> : <>
            <label style={{ display:"flex", flexDirection:"column", alignItems:"flex-start", gap:6, marginTop:8, width:"100%" }}>{STORE[storeId] || storeId}の金額（円）
              <input className="person-billing-amount-input" aria-label={`${person.person_name_snapshot} ${STORE[storeId] || storeId}金額`} type="number" inputMode="numeric" min="0" step="1" disabled={locked || loading} value={amounts[person.id] ?? ""} onKeyDown={preventAmountStepKey} onWheel={event=>event.currentTarget.blur()} onChange={event=>setAmounts(current=>({ ...current, [person.id]:event.target.value }))} style={amountInputStyle} />
            </label>
            <div style={{ marginTop:5, fontSize:18, fontWeight:800 }}>人物月次合計：{Number(person.current_total_amount || 0).toLocaleString()}円</div>
            {(person.store_amounts || []).length > 0 && <ul aria-label={`${person.person_name_snapshot} 施設別内訳`} style={{ margin:"6px 0 0", paddingLeft:20 }}>
              {person.store_amounts.map(row=><li key={row.store_id}>{STORE[row.store_id] || row.store_id}：{Number(row.amount) === 0 ? "利用なし・請求なし" : `${Number(row.amount).toLocaleString()}円`}</li>)}
            </ul>}
            {(person.missing_store_ids || []).length > 0 && <div role="status" style={{ color:"#b45309", marginTop:5 }}>未入力施設：{person.missing_store_ids.map(id=>STORE[id] || id).join("、")}</div>}
            {(person.no_charge_store_ids || []).includes(storeId) && <div role="status" style={{ color:"#1d4ed8", marginTop:5 }}>{STORE[storeId] || storeId}：利用なし・請求なし確認済み</div>}
            <div style={{ fontSize:12, color:"#475569", marginTop:5 }}>集計：{person.collected_by_name || "未入力"}／{time(person.collected_at)}　承認：{person.confirmed_by_name || "未承認"}／{time(person.confirmed_at)}</div>
            {person.entered_amount !== null && !approved && <div role="status" style={{ color:"#b45309", marginTop:5 }}>社長の最終承認待ちです。金額変更後は再承認が必要です。</div>}
            <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:8 }}>
              {!locked && <button type="button" disabled={loading || amounts[person.id] === ""} onClick={()=>mutate({ action:"collect_store", id:person.id, store_id:storeId, expected_revision:person.revision, expected_store_revision:storeAmount?.revision ?? null, amount:Number(amounts[person.id]), amount_edit:true, reason:"施設別月次金額の入力" })}>施設金額を保存</button>}
              {!locked && !storeAmount && <button type="button" disabled={loading} onClick={()=>{
                if (!confirmNoCharge(STORE[storeId] || storeId)) return;
                mutate({ action:"collect_store", id:person.id, store_id:storeId, expected_revision:person.revision, expected_store_revision:null, amount:0, no_charge:true, reason:"利用なし・請求なしの明示確認" });
              }}>{STORE[storeId] || storeId}を利用なし・請求なしにする</button>}
              {canFinalize && !locked && person.finalization_ready && !approved && <button type="button" disabled={loading} onClick={()=>mutate({ action:"finalize", id:person.id, expected_revision:person.revision, status:Number(person.current_total_amount) === 0 ? "no_charge" : "amount_confirmed" })}>社長最終承認</button>}
              {data.invoice_month?.status === "open" && person.billing_status === "unpaid" && terminalApproval && <button type="button" disabled={loading} onClick={()=>{
                if (!confirmResetApproval()) return;
                mutate({ action:"reset", id:person.id, expected_revision:person.revision });
              }}>承認を取り消す</button>}
              {!billingProhibited && person.billing_status === "unpaid" && approved && <button type="button" disabled={loading} onClick={()=>mutate({ action:"transition", id:person.id, expected_revision:person.revision, billing_status:"billed", reason:"請求済み登録" })}>請求済みにする</button>}
              {!billingProhibited && person.billing_status === "billed" && <button type="button" disabled={loading} onClick={()=>mutate({ action:"transition", id:person.id, expected_revision:person.revision, billing_status:"overdue", reason:"未収登録" })}>未収にする</button>}
              {!billingProhibited && ["billed","overdue"].includes(person.billing_status) && <>
                <select aria-label={`${person.person_name_snapshot} 入金方法`} value={payments[person.id] || "bank_transfer"} onChange={event=>setPayments(current=>({ ...current, [person.id]:event.target.value }))}>
                  {Object.entries(PAYMENT).map(([value,label])=><option key={value} value={value}>{label}</option>)}
                </select>
                <button type="button" disabled={loading} onClick={()=>mutate({ action:"transition", id:person.id, expected_revision:person.revision, billing_status:"paid", payment_method:payments[person.id] || "bank_transfer", reason:"入金確認" })}>入金済みにする</button>
              </>}
            </div>
            <div style={{ fontSize:12, color:"#475569", marginTop:5 }}>請求：{person.billed_by_name || "—"}／{time(person.billed_at)}　入金確認：{person.payment_confirmed_by_name || "—"}／{time(person.payment_confirmed_at)}{person.payment_method ? `（${PAYMENT[person.payment_method]}）` : ""}</div>
            {person.stale && <div role="alert" style={{ color:"#b45309" }}>承認後に根拠が変わりました。再承認してください。</div>}
          </>}
          </div>}
        </article>;
      })}
    </div>
  </section>;
}
